import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/shared/components/ui/button';
import { ListView } from '@/shared/components/custom/list-view/components/ListView';
import type { LinearScaleOptions } from '../../adapters/field-type.adapter';
import type { FieldAnalytics } from '../../types';
import type { FieldResponsesTarget } from './FieldResponsesDialog';

const BAR_COLORS = [
  'bg-red-500',
  'bg-orange-500',
  'bg-yellow-400',
  'bg-green-500',
  'bg-blue-500',
  'bg-purple-500',
];

interface FieldAnalyticsCardProps {
  field: FieldAnalytics;
  /** Only meaningful when `field.fieldType === 'LinearScale'` — see `FormAnalyticsDashboard`. */
  scaleOptions?: LinearScaleOptions;
  onDrillDown: (target: FieldResponsesTarget) => void;
}

export function FieldAnalyticsCard({ field, scaleOptions, onDrillDown }: FieldAnalyticsCardProps) {
  const { t } = useTranslation('forms');
  const isPlainCount = !field.optionCounts && !field.numericStats && !field.gridCounts;

  const scaleAverage =
    field.fieldType === 'LinearScale' ? (field.numericStats?.average ?? null) : null;
  const scaleTotalStars = scaleOptions ? scaleOptions.max - scaleOptions.min + 1 : 0;
  const scaleFilledStars =
    scaleAverage !== null && scaleOptions
      ? Math.min(scaleTotalStars, Math.max(0, Math.round(scaleAverage - scaleOptions.min) + 1))
      : 0;

  return (
    <div
      className="space-y-2 rounded-xl border p-4"
      data-testid={`forms-analytics-field-${field.fieldId}`}
    >
      <div className="flex min-w-0 items-baseline gap-2">
        <p className="text-sm font-semibold truncate">{field.label}</p>
        <span className="shrink-0 text-xs text-muted-foreground">
          {t(`builder.fieldTypes.${field.fieldType}`, { defaultValue: field.fieldType })}
          {field.required ? ` · ${t('builder.requiredLabel')}` : ''}
        </span>
      </div>

      {field.optionCounts && (
        <ul className="space-y-2">
          {field.optionCounts.map((opt, index) => (
            <li key={opt.option} className="flex items-center gap-3">
              <span className="w-28 shrink-0 truncate text-sm">{opt.option}</span>
              <div className="h-2 flex-1 rounded-full bg-muted">
                <div
                  className={`h-2 rounded-full ${BAR_COLORS[index % BAR_COLORS.length]}`}
                  style={{ width: `${Math.max(opt.percentage, 2)}%` }}
                />
              </div>
              <span className="w-16 shrink-0 text-right text-xs text-muted-foreground">
                {opt.count} · {opt.percentage}%
              </span>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto shrink-0 p-0 text-xs"
                onClick={() =>
                  onDrillDown({
                    fieldId: field.fieldId,
                    label: field.label,
                    fieldType: field.fieldType,
                    filterOption: opt.option,
                    filterLabel: opt.option,
                  })
                }
                data-testid={`forms-analytics-view-who-${field.fieldId}-${opt.option}`}
              >
                {t('submissions.analytics.viewWho')}
              </Button>
            </li>
          ))}
        </ul>
      )}

      {field.numericStats && field.fieldType === 'LinearScale' && scaleOptions && (
        <div>
          {scaleAverage === null ? (
            <p className="text-sm text-muted-foreground">—</p>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold">{scaleAverage}</span>
              <div className="flex items-center gap-0.5">
                {Array.from({ length: scaleTotalStars }, (_, i) => (
                  <Star
                    key={i}
                    className={
                      i < scaleFilledStars
                        ? 'h-3.5 w-3.5 fill-yellow-400 text-yellow-400'
                        : 'h-3.5 w-3.5 text-muted-foreground'
                    }
                  />
                ))}
              </div>
              <span className="text-xs text-muted-foreground">({field.totalResponses})</span>
            </div>
          )}
        </div>
      )}

      {field.numericStats && field.fieldType !== 'LinearScale' && (
        <div className="flex items-center gap-6 text-sm">
          <div>
            <p className="text-xs text-muted-foreground">{t('submissions.analytics.average')}</p>
            <p className="font-medium">{field.numericStats.average ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t('submissions.analytics.min')}</p>
            <p className="font-medium">{field.numericStats.min ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">{t('submissions.analytics.max')}</p>
            <p className="font-medium">{field.numericStats.max ?? '—'}</p>
          </div>
        </div>
      )}

      {field.gridCounts &&
        (() => {
          const gridCounts = field.gridCounts;
          const columns = Array.from(new Set(gridCounts.map((c) => c.column)));
          const rows = Array.from(new Set(gridCounts.map((c) => c.row)));

          const rowColumns: ColumnDef<string, unknown>[] = [
            {
              id: 'row',
              accessorFn: (row) => row,
              header: '',
              meta: { cellClassName: 'flex-[1] min-w-0' },
              cell: ({ row }) => <span className="text-sm">{row.original}</span>,
            },
            ...columns.map(
              (column, index): ColumnDef<string, unknown> => ({
                id: `col-${index}`,
                accessorFn: (row) => row,
                header: column,
                meta: { cellClassName: 'flex-1 min-w-0 text-center' },
                cell: ({ row }) => {
                  const cell = gridCounts.find(
                    (c) => c.row === row.original && c.column === column,
                  );
                  return (
                    <div className="w-full">
                      <div className="mx-auto mb-1 h-1.5 w-full max-w-16 rounded-full bg-muted">
                        <div
                          className="h-1.5 rounded-full bg-primary"
                          style={{ width: `${cell?.percentage ?? 0}%` }}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {cell ? `${cell.count} · ${cell.percentage}%` : '—'}
                      </span>
                    </div>
                  );
                },
              }),
            ),
          ];

          return (
            <ListView
              data={rows}
              columns={rowColumns}
              getRowId={(row) => row}
              className="rounded-none border-none bg-transparent"
              data-testid={`forms-analytics-grid-${field.fieldId}`}
            />
          );
        })()}

      {isPlainCount && (
        <p className="text-sm text-muted-foreground">
          {t('submissions.analytics.responseCount', { count: field.totalResponses })}
        </p>
      )}
    </div>
  );
}
